import React, { useState } from "react";
import { useEffect } from "react";
import {
  MdEdit,
  MdSettings,
  MdContentCopy,
  MdStore,
  MdReceipt,
  MdAttachMoney,
  MdArrowForward,
  MdStar,
  MdCheckCircle,
  MdCancel,
  MdSearch,
} from "react-icons/md";
import { useParams } from "react-router-dom";
import { getBookings, updateBookingStatus } from "../../services/booking.service";
import { getEmployee } from "../../services/employee.service";
import { t } from "i18next";
import { toast } from "react-toastify";

// Modal component - Made responsive with proper dark mode
function Modal({ open, onClose, title, children, actions }) {
  if (!open) return null;
  return (
    <div className='fixed z-40 inset-0 bg-black/40 dark:bg-black/70 flex items-center justify-center backdrop-blur-sm p-4'>
      <div className='bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto'>
        <div className='px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-white dark:from-gray-800 dark:to-gray-900 sticky top-0 z-10'>
          <div className='flex items-center justify-between'>
            <h2 className='text-lg sm:text-xl font-bold text-gray-800 dark:text-white'>{title}</h2>
            <button
              onClick={onClose}
              className='text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-white text-2xl font-bold transition-colors'
            >
              &times;
            </button>
          </div>
        </div>
        <div className='px-4 sm:px-6 py-4 bg-white dark:bg-gray-900'>{children}</div>
        {actions && (
          <div className='px-4 sm:px-6 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex flex-col sm:flex-row justify-end gap-3 rounded-b-xl'>
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}

// View Only Modal for Completed bookings
function ViewOnlyModal({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div className='fixed z-40 inset-0 bg-black/40 dark:bg-black/70 flex items-center justify-center backdrop-blur-sm p-4'>
      <div className='bg-white dark:bg-gray-900 rounded-xl shadow-2xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto'>
        <div className='px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-green-50 to-white dark:from-gray-800 dark:to-gray-900 sticky top-0 z-10'>
          <div className='flex items-center justify-between'>
            <div className="flex items-center gap-2">
              <MdCheckCircle className="text-green-600 dark:text-green-400 text-xl" />
              <h2 className='text-lg sm:text-xl font-bold text-gray-800 dark:text-white'>Booking Details (Completed)</h2>
            </div>
            <button
              onClick={onClose}
              className='text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-white text-2xl font-bold transition-colors'
            >
              &times;
            </button>
          </div>
        </div>
        <div className='px-4 sm:px-6 py-4 bg-white dark:bg-gray-900'>{children}</div>
        <div className='px-4 sm:px-6 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex justify-end rounded-b-xl'>
          <button
            onClick={onClose}
            className='px-6 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white font-medium transition-colors'
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default function OnlineServiceOrdersPage() {
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [copied, setCopied] = useState(false);
  const [orders, setOrders] = useState([]);
  const [employees, setEmployees] = useState([]);
  const { bussinessUuid } = useParams();
  const [updatingId, setUpdatingId] = useState(null);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [viewOnlyBooking, setViewOnlyBooking] = useState(null);
  const [employeeForm, setEmployeeForm] = useState({
    employeeId: "",
    employeeName: "",
    employeeChangeReason: "",
  });

  // Search and Pagination state
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  // Store Settings dialog state
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const allOrders = await getBookings(bussinessUuid);
        setOrders(allOrders);
        setTotalPages(Math.ceil(allOrders.length / limit));
      } catch (error) {
        console.error("Failed to fetch orders:", error);
      }
    };

    fetchOrders();
  }, [bussinessUuid]);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await getEmployee(bussinessUuid, { limit: 300 });
        const rows = response?.data?.data || response?.data || [];
        setEmployees(Array.isArray(rows) ? rows : []);
      } catch (error) {
        console.error("Failed to fetch employees:", error);
        setEmployees([]);
      }
    };

    if (bussinessUuid) {
      fetchEmployees();
    }
  }, [bussinessUuid]);

  useEffect(() => {
    if (!pendingStatusChange?.booking) {
      setEmployeeForm({
        employeeId: "",
        employeeName: "",
        employeeChangeReason: "",
      });
      return;
    }

    setEmployeeForm({
      employeeId: pendingStatusChange.booking.employeeId || "",
      employeeName: pendingStatusChange.booking.employeeName || "",
      employeeChangeReason: pendingStatusChange.booking.employeeChangeReason || "",
    });
  }, [pendingStatusChange]);

  // Filter orders based on search
  const filteredOrders = orders.filter((booking) => {
    const searchTerm = search.toLowerCase().trim();
    if (!searchTerm) return true;
    
    return (
      (booking.serviceName?.toLowerCase().includes(searchTerm)) ||
      (booking.name?.toLowerCase().includes(searchTerm)) ||
      (booking.mobile?.includes(searchTerm)) ||
      (booking.email?.toLowerCase().includes(searchTerm)) ||
      (booking._id?.toLowerCase().includes(searchTerm))
    );
  });

  // Paginate orders
  const paginatedOrders = filteredOrders.slice(
    (page - 1) * limit,
    page * limit
  );

  // Update total pages when filtered orders change
  useEffect(() => {
    setTotalPages(Math.ceil(filteredOrders.length / limit) || 1);
    if (page > Math.ceil(filteredOrders.length / limit) && filteredOrders.length > 0) {
      setPage(1);
    }
  }, [filteredOrders, limit]);

  const handleOpenInvoiceDialog = (order) => {
    setSelectedOrder(order);
    setInvoiceDialogOpen(true);
  };

  const handleVisitOnlineServices = () => {
    const storeUrl = `${window.location.origin}/store/${bussinessUuid}/?tab=services`;
    window.open(storeUrl, '_blank');
  };

  const handleCopyStoreLink = async () => {
    const storeUrl = `${window.location.origin}/store/${bussinessUuid}/`;
    try {
      await navigator.clipboard.writeText(storeUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      setCopied(false);
    }
  };

  const handleCloseInvoiceDialog = () => {
    setInvoiceDialogOpen(false);
    setSelectedOrder(null);
  };

  const handleStatusChange = async (bookingId, status, payload = {}) => {
    if (!bussinessUuid || !bookingId) return;
    setUpdatingId(bookingId);
    try {
      const response = await updateBookingStatus(bussinessUuid, bookingId, {
        status,
        ...payload,
      });
      const updatedBooking = response?.booking;
      setOrders((prev) =>
        prev.map((item) =>
          item._id === bookingId
            ? {
                ...item,
                ...(updatedBooking || {}),
                status: updatedBooking?.status || status,
              }
            : item
        )
      );

      if (status === "Confirmed") {
        if (response?.taskAssigned || response?.assignedTaskId) {
          toast.success("Booking confirmed and task assigned to employee.");
        } else {
          toast.success("Booking confirmed.");
        }
      } else if (status === "Cancelled") {
        toast.success("Booking cancelled and customer notified.");
      } else {
        toast.success("Booking status updated.");
      }
    } catch (error) {
      console.error("Failed to update booking status", error);
      toast.error(error?.response?.data?.message || "Failed to update booking status");
    } finally {
      setUpdatingId(null);
    }
  };

  const requestStatusChange = (booking, status) => {
    if (booking.status === "Completed") {
      toast.info("This booking is already completed.");
      return;
    }
    
    if (["Confirmed", "Cancelled"].includes(status)) {
      setPendingStatusChange({ booking, status });
      return;
    }

    handleStatusChange(booking._id, status);
  };

  const openBookingActionModal = (booking) => {
    if (!booking?._id) return;

    if (booking.status === "Completed") {
      setViewOnlyBooking(booking);
      return;
    }

    setPendingStatusChange({
      booking,
      status: String(booking.status || "Pending").toLowerCase() === "cancelled" ? "Cancelled" : "Confirmed",
    });
  };

  const closeStatusConfirmModal = () => {
    setPendingStatusChange(null);
  };

  const closeViewOnlyModal = () => {
    setViewOnlyBooking(null);
  };

  const confirmStatusChange = async () => {
    if (!pendingStatusChange?.booking?._id || !pendingStatusChange?.status) return;
    const currentEmployeeId = String(
      pendingStatusChange.booking.employeeId || pendingStatusChange.booking.employeeID || ""
    ).trim();
    const selectedEmployeeId = String(employeeForm.employeeId || "").trim();
    const employeeChanged = Boolean(selectedEmployeeId) && selectedEmployeeId !== currentEmployeeId;

    if (employeeChanged && !String(employeeForm.employeeChangeReason || "").trim()) {
      toast.error("Please add a reason for changing the assigned employee.");
      return;
    }

    await handleStatusChange(pendingStatusChange.booking._id, pendingStatusChange.status, {
      employeeId: selectedEmployeeId,
      employeeName:
        employees.find((employee) => String(employee.employeeID || employee.employeeId || employee._id) === String(employeeForm.employeeId))
          ?.employeeName || employeeForm.employeeName,
      employeeChangeReason: employeeForm.employeeChangeReason,
    });
    closeStatusConfirmModal();
  };

  function handleCreateInvoice() {
    alert("Invoice created!");
    handleCloseInvoiceDialog();
  }

  // Status badge component
  const StatusBadge = ({ status }) => {
    const statusColors = {
      Shipped: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
      Pending: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300",
      "In Progress": "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300",
      Delivered: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
      Processing: "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300",
      Completed: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
      Cancelled: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
    };

    return (
      <span
        className={`px-3 py-1 rounded-full text-sm font-medium ${
          statusColors[status] || "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
        }`}
      >
        {status}
      </span>
    );
  };

  // View Only Booking Details Component
  const ViewOnlyBookingDetails = ({ booking }) => (
    <div className='space-y-4 text-sm'>
      <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
        <MdCheckCircle className="text-green-600 dark:text-green-400 text-lg" />
        <p className="text-green-700 dark:text-green-300 font-medium">
          This booking has been completed successfully.
        </p>
      </div>

      <div className='grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4'>
        {/* Service */}
        <div className="relative">
          <div className="rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 p-2 sm:p-3 pt-4 sm:pt-5">
            <label className="absolute -top-2 left-2 sm:left-3 px-1 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50">
              Service
            </label>
            <p className="text-gray-900 dark:text-gray-100 font-medium text-xs sm:text-sm truncate">
              {booking.serviceName || "N/A"}
            </p>
          </div>
        </div>

        {/* Amount */}
        <div className="relative">
          <div className="rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 p-2 sm:p-3 pt-4 sm:pt-5">
            <label className="absolute -top-2 left-2 sm:left-3 px-1 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50">
              Amount
            </label>
            <p className="text-gray-900 dark:text-gray-100 font-medium text-xs sm:text-sm">
              ₹{Number(booking.amount || 0).toFixed(2)}
            </p>
          </div>
        </div>

        {/* Customer Name */}
        <div className="relative">
          <div className="rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 p-2 sm:p-3 pt-4 sm:pt-5">
            <label className="absolute -top-2 left-2 sm:left-3 px-1 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50">
              Customer Name
            </label>
            <p className="text-gray-900 dark:text-gray-100 font-medium text-xs sm:text-sm truncate">
              {booking.name || "N/A"}
            </p>
          </div>
        </div>

        {/* Customer Mobile */}
        <div className="relative">
          <div className="rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 p-2 sm:p-3 pt-4 sm:pt-5">
            <label className="absolute -top-2 left-2 sm:left-3 px-1 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50">
              Customer Mobile
            </label>
            <p className="text-gray-900 dark:text-gray-100 font-medium text-xs sm:text-sm">
              {booking.mobile || "N/A"}
            </p>
          </div>
        </div>

        {/* Customer Email */}
        <div className="relative sm:col-span-2">
          <div className="rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 p-2 sm:p-3 pt-4 sm:pt-5">
            <label className="absolute -top-2 left-2 sm:left-3 px-1 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50">
              Customer Email
            </label>
            <p className="text-gray-900 dark:text-gray-100 font-medium text-xs sm:text-sm break-all">
              {booking.email || "N/A"}
            </p>
          </div>
        </div>

        {/* Slot */}
        <div className="relative sm:col-span-2">
          <div className="rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 p-2 sm:p-3 pt-4 sm:pt-5">
            <label className="absolute -top-2 left-2 sm:left-3 px-1 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50">
              Slot
            </label>
            <p className="text-gray-900 dark:text-gray-100 font-medium text-xs sm:text-sm">
              {booking.date
                ? new Date(booking.date).toLocaleDateString("en-IN")
                : "N/A"}
              {" "}
              {booking.time || ""}
            </p>
          </div>
        </div>

        {/* Assigned Employee */}
        <div className="relative sm:col-span-2">
          <div className="rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 p-2 sm:p-3 pt-4 sm:pt-5">
            <label className="absolute -top-2 left-2 sm:left-3 px-1 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50">
              Assigned Employee
            </label>
            <p className="text-gray-900 dark:text-gray-100 font-medium text-xs sm:text-sm">
              {booking.employeeName || "Auto assign"}
            </p>
          </div>
        </div>

        {/* Status */}
        <div className="relative sm:col-span-2">
          <div className="rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 p-2 sm:p-3 pt-4 sm:pt-5">
            <label className="absolute -top-2 left-2 sm:left-3 px-1 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50">
              Status
            </label>
            <div className="mt-1">
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 text-xs sm:text-sm font-medium">
                <MdCheckCircle className="text-green-600 dark:text-green-400" />
                Completed
              </span>
            </div>
          </div>
        </div>

        {/* Payment Mode */}
        <div className="relative sm:col-span-2">
          <div className="rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 p-2 sm:p-3 pt-4 sm:pt-5">
            <label className="absolute -top-2 left-2 sm:left-3 px-1 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50">
              Payment Mode
            </label>
            <p className="text-gray-900 dark:text-gray-100 font-medium text-xs sm:text-sm">
              {booking.paymentMode || "N/A"}
            </p>
          </div>
        </div>

        {/* Review Section */}
        {booking?.reviewSubmittedAt && (
          <div className="relative sm:col-span-2">
            <div className="rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 p-2 sm:p-3 pt-4 sm:pt-5">
              <label className="absolute -top-2 left-2 sm:left-3 px-1 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50">
                Customer Review
              </label>
              <div className="mt-1 space-y-2">
                <div className="flex items-center gap-1 text-amber-500">
                  <MdStar size={18} />
                  <span className="font-semibold">
                    {Number(booking?.reviewRating || 0).toFixed(1)} / 5
                  </span>
                </div>
                {booking?.reviewComment && (
                  <p className="text-gray-700 dark:text-gray-300 text-xs sm:text-sm italic">
                    "{booking.reviewComment}"
                  </p>
                )}
                <p className="text-gray-400 dark:text-gray-500 text-xs">
                  Reviewed on: {new Date(booking.reviewSubmittedAt).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-900 py-8 px-4 sm:px-6 lg:px-8'>
      <div className='mx-auto max-w-7xl'>
      {/* DASHBOARD HEADER */}
      <div className='mb-8'>
        <div className='flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-8'>
          <div>
            <h1 className='text-2xl sm:text-3xl font-bold bg-indigo-600 bg-clip-text text-transparent flex items-center gap-3'>
              <MdStore className='text-blue-600' size={28} />
              {t("ONLINE_SERVICE.SERVICE_DASHBOARD")}
            </h1>
            <p className='mt-2 text-gray-600 dark:text-gray-300'>
              {t("ONLINE_SERVICE.MANAGE_YOUR_SERVICES")}
            </p>
          </div>

          <div className='flex flex-col sm:flex-row gap-3'>
            <button
              onClick={handleVisitOnlineServices}
              className='inline-flex items-center justify-center gap-2 px-4 sm:px-6 py-3 rounded-lg font-semibold bg-gradient-to-r from-green-600 to-emerald-500 text-white hover:from-green-700 hover:to-emerald-600 transition-all shadow-md hover:shadow-lg transform hover:scale-105 text-sm sm:text-base'
            >
              <MdStore size={20} />
              {t("ONLINE_SERVICE.VISIT_ONLINE_SERVICES")}
              <MdArrowForward size={18} />
            </button>

            <button
              onClick={handleCopyStoreLink}
              type='button'
              className={`inline-flex items-center justify-center gap-2 border px-4 sm:px-5 py-3 rounded-lg font-medium transition-all text-sm sm:text-base ${
                copied
                  ? "border-green-500 text-green-700 bg-green-50 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300 shadow-inner"
                  : "border-gray-300 text-gray-700 bg-white hover:bg-gray-50 shadow-sm hover:shadow-md dark:border-gray-600 dark:text-gray-300 dark:bg-gray-800 dark:hover:bg-gray-700"
              }`}
              aria-label='Copy store link'
            >
              <MdContentCopy size={20} />
              {copied ? t("ONLINE_SERVICE.COPIED") : t("ONLINE_SERVICE.COPY_LINK")}
            </button>
          </div>
        </div>

        {/* STATS CARDS */}
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8'>
          <div
            className='bg-white dark:bg-slate-800
                      p-6 rounded-2xl
                      shadow-sm dark:shadow-lg
                      border border-gray-100 dark:border-slate-700
                      transition-all duration-300 hover:shadow-lg hover:-translate-y-1'
          >
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide'>
                  {t("ONLINE_SERVICE.TOTAL_ORDERS")}
                </p>

                <p className='text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mt-2'>
                  {orders.length}
                </p>
              </div>

              <div
                className='p-4 rounded-2xl
                          bg-gradient-to-br from-blue-100 to-blue-50 dark:from-blue-900/40 dark:to-blue-800/30
                          text-blue-600 dark:text-blue-400'
              >
                <MdReceipt size={28} />
              </div>
            </div>
          </div>


                    <div
            className='bg-white dark:bg-slate-800
                      p-6 rounded-2xl
                      shadow-sm dark:shadow-lg
                      border border-gray-100 dark:border-slate-700
                      transition-all duration-300 hover:shadow-lg hover:-translate-y-1'
          >
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide'>
                  {t("ONLINE_SERVICE.REVENUE")}
                </p>

                <p className='text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mt-2'>
                  ₹{orders

                    .reduce((sum, order) => sum + order.amount, 0)
                    .toFixed(2)}
                </p>
              </div>

              <div
                className='p-4 rounded-2xl
                          bg-gradient-to-br from-green-100 to-emerald-50 dark:from-green-900/40 dark:to-emerald-800/30
                          text-green-600 dark:text-green-400'
              >
                <MdAttachMoney size={28} />
              </div>
            </div>
          </div>


                    <div
            className='bg-white dark:bg-slate-800
                      p-6 rounded-2xl
                      shadow-sm dark:shadow-lg
                      border border-gray-100 dark:border-slate-700
                      transition-all duration-300 hover:shadow-lg hover:-translate-y-1'
          >
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide'>
                  {t("ONLINE_SERVICE.AVG_ORDER_VALUE")}
                </p>

                <p className='text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mt-2'>
                  ₹{orders.length > 0

                    ? (
                        orders.reduce((sum, order) => sum + order.amount, 0) /
                        orders.length
                      ).toFixed(2)
                    : "0.00"}
                </p>
              </div>

              <div
                className='p-4 rounded-2xl
                          bg-gradient-to-br from-purple-100 to-pink-50 dark:from-purple-900/40 dark:to-pink-800/30
                          text-purple-600 dark:text-purple-400'
              >
                <MdEdit size={28} />
              </div>
            </div>
          </div>
        </div>

        <div className='bg-white dark:bg-slate-800 shadow-lg rounded-2xl overflow-hidden border border-gray-100 dark:border-slate-700'>
            <div className='px-4 sm:px-6 py-5 border-b border-gray-100 dark:border-slate-700 bg-gradient-to-r from-gray-50 to-white dark:from-slate-800 dark:to-slate-900 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4'>
              <div className="flex items-center gap-3">
                <h2 className='text-lg font-bold text-gray-900 dark:text-white'>
                  {t("ONLINE_SERVICE.RECENT_BOOKINGS")}
                </h2>
                <span className='inline-block px-3 py-1 text-xs font-semibold text-blue-700 bg-blue-100 dark:bg-blue-900/40 dark:text-blue-300 rounded-full'>
                  {filteredOrders.length} {filteredOrders.length === 1 ? 'booking' : 'bookings'}
                </span>
              </div>
              
              {/* Search Input - Added */}
              <div className='relative w-full sm:w-64'>
                <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                  <MdSearch className='h-4 w-4 text-gray-400 dark:text-gray-500' />
                </div>
                <input
                  type='text'
                  placeholder='Search bookings...'
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className='w-full pl-10 pr-4 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all'
                />
              </div>
            </div>

            {/* Table with Vertical Scroll - Made responsive */}
            <div className='overflow-x-auto'>
              <div className='max-h-[500px] overflow-y-auto'>
                <table className='min-w-full divide-y divide-gray-100 dark:divide-slate-700'>
                  <thead className='bg-gray-50 dark:bg-slate-700/100 sticky top-0 z-10 border-b-2 border-gray-200 dark:border-slate-600'>
                    <tr>
                      <th className='py-3 px-3 sm:py-4 sm:px-6 text-left text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider'>
                        {t("COMMON.SERVICE")}
                      </th>
                      <th className='py-3 px-3 sm:py-4 sm:px-6 text-left text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider'>
                        {t("COMMON.CUSTOMER")}
                      </th>
                      <th className='py-3 px-3 sm:py-4 sm:px-6 text-left text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider hidden sm:table-cell'>
                        {t("SIDER.DATE")}
                      </th>
                      <th className='py-3 px-3 sm:py-4 sm:px-6 text-left text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider hidden sm:table-cell'>
                        {t("SIDER.TIME")}
                      </th>
                      <th className='py-3 px-3 sm:py-4 sm:px-6 text-left text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider hidden md:table-cell'>
                        {t("CARD.PAYMENT")}
                      </th>
                      <th className='py-3 px-3 sm:py-4 sm:px-6 text-left text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider'>
                        Status
                      </th>
                      <th className='py-3 px-3 sm:py-4 sm:px-6 text-left text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider hidden lg:table-cell'>
                        Review
                      </th>
                      <th className='py-3 px-3 sm:py-4 sm:px-6 text-left text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider'>
                        Action
                      </th>
                      <th className='py-3 px-3 sm:py-4 sm:px-6 text-right text-xs font-bold text-gray-700 dark:text-gray-200 uppercase tracking-wider'>
                        {t("SIDER.AMOUNT")} (₹)
                      </th>
                    </tr>
                  </thead>

                  <tbody className='bg-white dark:bg-slate-900 divide-y divide-gray-100 dark:divide-slate-700'>
                    {paginatedOrders.length === 0 ? (
                      <tr>
                        <td colSpan={9} className='py-12 px-6 text-center'>
                          <div className='flex flex-col items-center justify-center'>
                            <div className='p-4 rounded-full bg-gray-100 dark:bg-slate-800 mb-4'>
                              <MdReceipt size={32} className='text-gray-400 dark:text-slate-600' />
                            </div>
                            <p className='text-lg font-semibold text-gray-600 dark:text-gray-300'>
                              {search ? "No bookings match your search" : t("ONLINE_SERVICE.NO_BOOKINGS_YET")}
                            </p>
                            <p className='text-sm text-gray-500 dark:text-gray-400 mt-1'>
                              {search ? "Try adjusting your search terms" : t("ONLINE_SERVICE.YOUR_SERVICE_BOOKING_WILL_APPEAR_HERE")}
                            </p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      paginatedOrders.map((booking) => {
                        const isCompleted = booking.status === "Completed";
                        return (
                          <tr
                            key={booking._id}
                            className={`hover:bg-blue-50 dark:hover:bg-slate-800/50 transition-colors ${isCompleted ? 'opacity-75' : ''}`}
                          >
                            <td className='py-3 px-3 sm:py-4 sm:px-6 whitespace-nowrap font-semibold text-gray-900 dark:text-white text-sm'>
                              {booking.serviceName}
                            </td>

                            <td className='py-3 px-3 sm:py-4 sm:px-6 whitespace-nowrap'>
                              <div className='flex items-center'>
                                <div className='flex-shrink-0 h-8 w-8 sm:h-10 sm:w-10 bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900/40 dark:to-blue-800/40 rounded-full flex items-center justify-center text-blue-700 dark:text-blue-400 font-bold text-xs sm:text-sm'>
                                  {booking.name?.charAt(0)?.toUpperCase()}
                                </div>
                                <div className='ml-2 sm:ml-3'>
                                  <div className='text-xs sm:text-sm font-semibold text-gray-900 dark:text-white'>
                                    {booking.name}
                                  </div>
                                  <div className='text-xs text-gray-500 dark:text-gray-400'>
                                    {booking.mobile}
                                  </div>
                                </div>
                              </div>
                            </td>

                            <td className='py-3 px-3 sm:py-4 sm:px-6 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400 hidden sm:table-cell'>
                              {new Date(booking.date).toLocaleDateString('en-IN')}
                            </td>

                            <td className='py-3 px-3 sm:py-4 sm:px-6 whitespace-nowrap text-sm font-medium text-gray-700 dark:text-gray-300 hidden sm:table-cell'>
                              {booking.time}
                            </td>

                            <td className='py-3 px-3 sm:py-4 sm:px-6 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400 hidden md:table-cell'>
                              <span className='inline-block px-2.5 py-1 rounded-full bg-gray-100 dark:bg-slate-700 text-xs font-medium'>
                                {booking.paymentMode || "N/A"}
                              </span>
                            </td>

                            <td className='py-3 px-3 sm:py-4 sm:px-6 whitespace-nowrap'>
                              <select
                                value={booking.status || "Pending"}
                                onChange={(e) => requestStatusChange(booking, e.target.value)}
                                disabled={updatingId === booking._id || isCompleted}
                                className={`rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-2 sm:px-3 py-1.5 text-xs sm:text-sm text-gray-700 dark:text-gray-200 shadow-sm focus:border-blue-500 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60 transition-colors ${isCompleted ? 'cursor-not-allowed opacity-60' : ''}`}
                              >
                                <option value="Pending">Pending</option>
                                <option value="Confirmed">Confirmed</option>
                                <option value="In Progress">In Progress</option>
                                <option value="Completed">Completed</option>
                                <option value="Cancelled">Cancelled</option>
                              </select>
                              {isCompleted && (
                                <span className="ml-1 text-xs text-green-600 dark:text-green-400 font-medium">
                                  ✓
                                </span>
                              )}
                            </td>

                            <td className='py-3 px-3 sm:py-4 sm:px-6 text-sm text-gray-600 dark:text-gray-300 hidden lg:table-cell'>
                              {booking?.reviewSubmittedAt ? (
                                <div className='max-w-[220px]'>
                                  <p className='flex items-center gap-1 text-amber-500 font-semibold'>
                                    <MdStar size={16} />
                                    {Number(booking?.reviewRating || 0).toFixed(1)} / 5
                                  </p>
                                  <p className='mt-1 text-xs text-gray-500 dark:text-gray-400 line-clamp-2'>
                                    {booking?.reviewComment || "Review submitted"}
                                  </p>
                                </div>
                              ) : (
                                <span className='inline-block rounded-full bg-gray-100 dark:bg-slate-700 px-2 py-1 text-xs'>
                                  No review
                                </span>
                              )}
                            </td>

                            <td className='py-3 px-3 sm:py-4 sm:px-6 whitespace-nowrap'>
                              <button
                                type='button'
                                onClick={() => openBookingActionModal(booking)}
                                className={`px-2 sm:px-3 py-1.5 rounded-lg border text-xs font-semibold transition-colors ${
                                  isCompleted
                                    ? 'border-green-300 bg-green-50 text-green-700 cursor-pointer hover:bg-green-100 dark:border-green-800/40 dark:bg-green-900/20 dark:text-green-400 dark:hover:bg-green-900/30'
                                    : 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:border-blue-900/40 dark:bg-blue-900/20 dark:text-blue-300 dark:hover:bg-blue-900/30'
                                }`}
                              >
                                {isCompleted ? (
                                  <span className="flex items-center gap-1">
                                    <MdCheckCircle size={14} />
                                    View Details
                                  </span>
                                ) : String(booking.status || "Pending").toLowerCase() === "confirmed"
                                  ? "Update Employee"
                                  : "Confirm / Update"}
                              </button>
                            </td>

                            <td className='py-3 px-3 sm:py-4 sm:px-6 whitespace-nowrap text-right text-sm font-bold text-emerald-600 dark:text-emerald-400'>
                              ₹{Number(booking.amount || 0).toFixed(2)}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination - Added */}
            {filteredOrders.length > 0 && (
              <div className='px-4 sm:px-6 py-4 border-t border-gray-100 dark:border-slate-700 flex flex-col sm:flex-row justify-between items-center gap-3 bg-gray-50 dark:bg-slate-800/50'>
                <div className='text-sm text-gray-500 dark:text-gray-400'>
                  Showing {(page - 1) * limit + 1} to {Math.min(page * limit, filteredOrders.length)} of {filteredOrders.length} bookings
                </div>
                <div className='flex items-center gap-2'>
                  <button
                    onClick={() => setPage((p) => Math.max(p - 1, 1))}
                    disabled={page === 1}
                    className='px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm'
                  >
                    Previous
                  </button>
                  <span className='text-sm font-medium text-gray-700 dark:text-gray-300 px-3'>
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                    disabled={page === totalPages}
                    className='px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm'
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

  <Modal
  open={Boolean(pendingStatusChange)}
  onClose={closeStatusConfirmModal}
  title={pendingStatusChange?.status === "Confirmed" ? t("NEW_ONLINE_SERVICE.CONFIRM_SERVICE_BOOKING") : t("NEW_ONLINE_SERVICE.CANCEL_SERVICE_BOOKING")}
  actions={[
    <button
      key='cancel'
      onClick={closeStatusConfirmModal}
      className='px-4 sm:px-6 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 w-full sm:w-auto font-medium'
    >
      {t("NEW_ONLINE_SERVICE.BACK")}
    </button>,
    <button
      key='confirm'
      onClick={confirmStatusChange}
      disabled={updatingId === pendingStatusChange?.booking?._id}
      className={`px-4 sm:px-6 py-2.5 rounded-lg text-white font-semibold transition-all disabled:opacity-60 disabled:cursor-not-allowed w-full sm:w-auto shadow-md hover:shadow-lg ${
        pendingStatusChange?.status === "Confirmed"
          ? "bg-green-600 hover:bg-green-700 dark:bg-green-600 dark:hover:bg-green-700"
          : "bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700"
      }`}
    >
      {updatingId === pendingStatusChange?.booking?._id
        ? t("NEW_ONLINE_SERVICE.PROCESSING")
        : pendingStatusChange?.status === "Confirmed"
          ? t("NEW_ONLINE_SERVICE.CONFIRM_BOOKING")
          : t("NEW_ONLINE_SERVICE.CANCEL_BOOKING")}
    </button>,
  ]}
>
  {pendingStatusChange?.booking && (
    <div className='space-y-4 text-sm'>
      <p className='text-gray-600 dark:text-gray-400 text-sm'>
        {t("NEW_ONLINE_SERVICE.REVIEW_FULL_BOOKING_DETAILS")}
      </p>

      {/* Responsive Grid */}
      <div className='grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4'>
        {/* Service */}
        <div className="relative">
          <div className="rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 p-2 sm:p-3 pt-4 sm:pt-5">
            <label className="absolute -top-2 left-2 sm:left-3 px-1 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50">
              {t("NEW_ONLINE_SERVICE.SERVICE")}
            </label>
            <p className="text-gray-900 dark:text-gray-100 font-medium text-xs sm:text-sm truncate">
              {pendingStatusChange.booking.serviceName || "N/A"}
            </p>
          </div>
        </div>

        {/* Amount */}
        <div className="relative">
          <div className="rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 p-2 sm:p-3 pt-4 sm:pt-5">
            <label className="absolute -top-2 left-2 sm:left-3 px-1 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50">
              {t("NEW_ONLINE_SERVICE.AMOUNT")}
            </label>
            <p className="text-gray-900 dark:text-gray-100 font-medium text-xs sm:text-sm">
              ₹{Number(pendingStatusChange.booking.amount || 0).toFixed(2)}
            </p>
          </div>
        </div>

        {/* Customer Name */}
        <div className="relative">
          <div className="rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 p-2 sm:p-3 pt-4 sm:pt-5">
            <label className="absolute -top-2 left-2 sm:left-3 px-1 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50">
              {t("NEW_ONLINE_SERVICE.CUSTOMER_NAME")}
            </label>
            <p className="text-gray-900 dark:text-gray-100 font-medium text-xs sm:text-sm truncate">
              {pendingStatusChange.booking.name || "N/A"}
            </p>
          </div>
        </div>

        {/* Customer Mobile */}
        <div className="relative">
          <div className="rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 p-2 sm:p-3 pt-4 sm:pt-5">
            <label className="absolute -top-2 left-2 sm:left-3 px-1 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50">
              {t("NEW_ONLINE_SERVICE.CUSTOMER_MOBILE")}
            </label>
            <p className="text-gray-900 dark:text-gray-100 font-medium text-xs sm:text-sm">
              {pendingStatusChange.booking.mobile || "N/A"}
            </p>
          </div>
        </div>

        {/* Customer Email */}
        <div className="relative sm:col-span-2">
          <div className="rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 p-2 sm:p-3 pt-4 sm:pt-5">
            <label className="absolute -top-2 left-2 sm:left-3 px-1 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50">
              {t("NEW_ONLINE_SERVICE.CUSTOMER_EMAIL")}
            </label>
            <p className="text-gray-900 dark:text-gray-100 font-medium text-xs sm:text-sm break-all">
              {pendingStatusChange.booking.email || "N/A"}
            </p>
          </div>
        </div>

        {/* Slot */}
        <div className="relative sm:col-span-2">
          <div className="rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 p-2 sm:p-3 pt-4 sm:pt-5">
            <label className="absolute -top-2 left-2 sm:left-3 px-1 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50">
              {t("NEW_ONLINE_SERVICE.SLOT")}
            </label>
            <p className="text-gray-900 dark:text-gray-100 font-medium text-xs sm:text-sm">
              {pendingStatusChange.booking.date
                ? new Date(pendingStatusChange.booking.date).toLocaleDateString("en-IN")
                : "N/A"}
              {" "}
              {pendingStatusChange.booking.time || ""}
            </p>
          </div>
        </div>

        {/* Assigned Employee */}
        <div className="relative sm:col-span-2">
          <div className="rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 p-2 sm:p-3 pt-4 sm:pt-5">
            <label className="absolute -top-2 left-2 sm:left-3 px-1 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50">
              {t("NEW_ONLINE_SERVICE.ASSIGNED_EMPLOYEE")}
            </label>
            <p className="text-gray-900 dark:text-gray-100 font-medium text-xs sm:text-sm">
              {pendingStatusChange.booking.employeeName || "Auto assign"}
            </p>
          </div>
        </div>

        {/* Update Assigned Employee - Fixed Dropdown */}
        <div className="relative sm:col-span-2">
          <div className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-2 sm:p-3 pt-4 sm:pt-5">
            <label className="absolute -top-2 left-2 sm:left-3 px-1 text-xs font-medium text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 z-10">
              {t("NEW_ONLINE_SERVICE.UPDATE_ASSIGNED_EMPLOYEE")}
            </label>
            <select
              value={employeeForm.employeeId}
              onChange={(event) => setEmployeeForm((prev) => ({ ...prev, employeeId: event.target.value }))}
              className="w-full rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2.5 text-sm text-gray-900 dark:text-gray-100 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all cursor-pointer shadow-sm hover:border-blue-400"
            >
              <option value="" className="text-gray-500 dark:text-gray-400 py-1.5">
                {t("NEW_ONLINE_SERVICE.KEEP_CURRENT_ASSIGNMENT")}
              </option>
              {employees.map((employee) => {
                const employeeKey = employee.employeeID || employee.employeeId || employee._id;
                const employeeLabel = employee.employeeName || employee.name || employee.fullName || "Employee";
                return (
                  <option key={employeeKey} value={employeeKey} className="py-1.5 text-gray-900 dark:text-gray-100">
                    {employeeLabel}
                  </option>
                );
              })}
            </select>
            {/* Show selected employee badge */}
            {employeeForm.employeeId && (
              <div className="mt-2 flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-medium border border-blue-200 dark:border-blue-800">
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                  {employees.find(emp => 
                    (emp.employeeID || emp.employeeId || emp._id) === employeeForm.employeeId
                  )?.employeeName || employeeForm.employeeName || "Selected"}
                </span>
                <button
                  onClick={() => setEmployeeForm((prev) => ({ ...prev, employeeId: "", employeeName: "" }))}
                  className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Reason */}
        <div className="relative sm:col-span-2">
          <div className="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-2 sm:p-3 pt-4 sm:pt-5">
            <label className="absolute -top-2 left-2 sm:left-3 px-1 text-xs font-medium text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800">
              {t("NEW_ONLINE_SERVICE.REASON_FOR_CHANGING_EMPLOYEE")}
            </label>
            <textarea
              rows={2}
              value={employeeForm.employeeChangeReason}
              onChange={(event) => setEmployeeForm((prev) => ({ ...prev, employeeChangeReason: event.target.value }))}
              placeholder={t("NEW_ONLINE_SERVICE.WRITE_REASON_FOR_REASSIGNMENT")}
              className="w-full resize-none rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all placeholder-gray-400 dark:placeholder-gray-500"
            />
          </div>
        </div>

        {/* Payment Mode */}
        <div className="relative sm:col-span-2">
          <div className="rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 p-2 sm:p-3 pt-4 sm:pt-5">
            <label className="absolute -top-2 left-2 sm:left-3 px-1 text-xs font-medium text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50">
              {t("NEW_ONLINE_SERVICE.PAYMENT_MODE")}
            </label>
            <p className="text-gray-900 dark:text-gray-100 font-medium text-xs sm:text-sm">
              {pendingStatusChange.booking.paymentMode || "N/A"}
            </p>
          </div>
        </div>
      </div>

      {/* Status Message */}
      <div className={`rounded-lg p-3 sm:p-4 border ${
        pendingStatusChange.status === "Confirmed"
          ? "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-300"
          : "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-300"
      }`}>
        <div className="flex items-start gap-2">
          {pendingStatusChange.status === "Confirmed" ? (
            <svg className="w-4 h-4 sm:w-5 sm:h-5 mt-0.5 text-green-600 dark:text-green-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg className="w-4 h-4 sm:w-5 sm:h-5 mt-0.5 text-red-600 dark:text-red-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l-1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          )}
          <p className="text-xs sm:text-sm font-medium">
            {pendingStatusChange.status === "Confirmed"
              ? t("NEW_ONLINE_SERVICE.CONFIRMATION_MESSAGE")
              : t("NEW_ONLINE_SERVICE.CANCELLATION_MESSAGE")}
          </p>
        </div>
      </div>
    </div>
  )}
</Modal>
      {/* View Only Modal for Completed Bookings */}
      <ViewOnlyModal
        open={Boolean(viewOnlyBooking)}
        onClose={closeViewOnlyModal}
        title="Booking Details"
      >
        {viewOnlyBooking && <ViewOnlyBookingDetails booking={viewOnlyBooking} />}
      </ViewOnlyModal>
    </div>
  );
}